// stockExcelExport.js
// Usage: import { exportStockToExcel } from "./stockExcelExport";

import * as XLSX from "xlsx";

/**
 * Flattens a bike stock object into a flat row for Excel
 */
const flattenBike = (bike, locationName) => ({
  Location:         locationName || bike.location || "-",
  "Sl No":          bike.slNo || "-",
  Date:             bike.date ? new Date(bike.date).toLocaleDateString("en-IN") : "-",
  "Challan No":     bike.challanNo || "-",
  Model:            bike.model || "-",
  "SKU Code":       bike.skuCode || "-",
  Colour:           bike.colour || "-",
  "Chasis No":      bike.chasis || "-",
  "Engine No":      bike.engine || "-",
  "Key Number":     bike.keyNumber || "-",
  "Ex-Showroom (₹)": bike.exShowroomPrice ?? 0,
  "Purchase Price (₹)": bike.purchasePrice ?? 0,
  Status:           bike.status || "-",
  Source:           bike.source || "-",
  "Owner Name":     bike.ownerName || "-",
  "Reg Number":     bike.registrationNumber || "-",
  Mobile:           bike.mobile || "-",
  Remarks:          bike.remarks || "-",
  "Created At":     bike.createdAt ? new Date(bike.createdAt).toLocaleDateString("en-IN") : "-",
});

/**
 * exportStockToExcel
 * @param {Array}  groupedData  - array of { location, bikes[], total }
 * @param {Object} filters      - current filter state (for filename/title)
 */
export const exportStockToExcel = (groupedData, filters = {}) => {
  const wb = XLSX.utils.book_new();

  // ── 1. ALL BIKES SHEET ──────────────────────────────────────────────────────
  const allRows = [];
  groupedData.forEach((loc) => {
    loc.bikes.forEach((bike) => {
      allRows.push(flattenBike(bike, loc.location));
    });
  });

  if (allRows.length > 0) {
    const allWs = XLSX.utils.json_to_sheet(allRows);
    // Column widths
    allWs["!cols"] = [
      { wch: 14 }, { wch: 7 },  { wch: 12 }, { wch: 14 }, { wch: 24 },
      { wch: 10 }, { wch: 12 }, { wch: 20 }, { wch: 20 }, { wch: 12 },
      { wch: 16 }, { wch: 18 }, { wch: 10 }, { wch: 10 }, { wch: 14 },
      { wch: 14 }, { wch: 14 }, { wch: 20 }, { wch: 12 },
    ];
    XLSX.utils.book_append_sheet(wb, allWs, "All Stock");
  }

  // ── 2. PER-LOCATION SHEETS ──────────────────────────────────────────────────
  groupedData.forEach((loc) => {
    const rows = loc.bikes.map((bike) => flattenBike(bike, loc.location));
    if (rows.length === 0) return;

    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [
      { wch: 14 }, { wch: 7 },  { wch: 12 }, { wch: 14 }, { wch: 24 },
      { wch: 10 }, { wch: 12 }, { wch: 20 }, { wch: 20 }, { wch: 12 },
      { wch: 16 }, { wch: 18 }, { wch: 10 }, { wch: 10 }, { wch: 14 },
      { wch: 14 }, { wch: 14 }, { wch: 20 }, { wch: 12 },
    ];

    // Sheet name max 31 chars
    const sheetName = (loc.location || "Unknown").slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });

  // ── 3. SUMMARY SHEET ────────────────────────────────────────────────────────
  const summaryRows = groupedData.map((loc) => ({
    Location:       loc.location,
    "Total Bikes":  loc.total,
    "NEW Bikes":    loc.bikes.filter((b) => b.source === "NEW").length,
    "EXCHANGE Bikes": loc.bikes.filter((b) => b.source === "EXCHANGE").length,
    "Total Purchase Value (₹)": loc.bikes.reduce((s, b) => s + (b.purchasePrice || 0), 0),
  }));

  // Grand total row
  summaryRows.push({
    Location: "GRAND TOTAL",
    "Total Bikes": summaryRows.reduce((s, r) => s + r["Total Bikes"], 0),
    "NEW Bikes":   summaryRows.reduce((s, r) => s + r["NEW Bikes"], 0),
    "EXCHANGE Bikes": summaryRows.reduce((s, r) => s + r["EXCHANGE Bikes"], 0),
    "Total Purchase Value (₹)": summaryRows.reduce((s, r) => s + r["Total Purchase Value (₹)"], 0),
  });

  const summaryWs = XLSX.utils.json_to_sheet(summaryRows);
  summaryWs["!cols"] = [{ wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 24 }];
  XLSX.utils.book_append_sheet(wb, summaryWs, "Summary");

  // ── 4. FILENAME ─────────────────────────────────────────────────────────────
  const parts = ["Stock"];
  if (filters.location) parts.push(filters.location);
  if (filters.month)    parts.push(`Month-${filters.month}`);
  if (filters.year)     parts.push(`Year-${filters.year}`);
  if (filters.fromDate) parts.push(`From-${filters.fromDate}`);
  if (filters.toDate)   parts.push(`To-${filters.toDate}`);
  parts.push(new Date().toISOString().split("T")[0]);

  XLSX.writeFile(wb, `${parts.join("_")}.xlsx`);
};